/** Joint review of a reader study: two readers settle, together, the round-1
 * marks only one of them made. Each item is one such mark, shown as Reader A or
 * Reader B (never a name). Each reader makes a call on it; the item is final
 * when both calls match and the shared note (the reason) is not empty.
 *
 * A blind review shows each item as a marker (reader null) and runs in two
 * phases: independent, where each reader calls every item alone with a rating
 * (present, absent) or a reason (indeterminate) and nothing is shared; then
 * joint, which lists only the items whose independent calls differ. */

export type Reader = "A" | "B";
export type JointCall = "present" | "absent" | "indeterminate";
export type Editor = "you" | "other_reader";
export type Phase = "independent" | "joint";

/** The rating anchors of a blind review's present and absent calls. */
export const RATINGS: readonly { value: number; label: string }[] = [
  { value: 10, label: "Unlikely" },
  { value: 30, label: "Possibly" },
  { value: 50, label: "Probably" },
  { value: 70, label: "Likely" },
  { value: 90, label: "Definite" },
];

export const CALLS: readonly {
  value: JointCall;
  label: string;
  key: string;
}[] = [
  { value: "present", label: "Stone present", key: "1" },
  { value: "absent", label: "Not a stone", key: "2" },
  { value: "indeterminate", label: "Indeterminate", key: "3" },
];

export const NOTE_MAX_LENGTH = 500;
export const SESSION_NOTES_MAX_LENGTH = 5000;

export const callLabel = (call: JointCall) => CALLS.find(({ value }) => value === call)!.label;

/** An item in review order. `done`: nothing more is needed from this reader
 * (its own call in a blind review's independent phase, else final). */
export interface OrderEntry {
  study_uid: string;
  item_id: string;
  final: boolean;
  done: boolean;
}

/** GET /projects/{id}/joint-review: the viewed reader's items in review order.
 * In a blind review's joint phase, only the items whose independent calls
 * differ; `other_done` says whether the other reader has called every item. For
 * an admin observing (not a reader), the two readers in view order. */
export interface JointReviewProject {
  phase: Phase | null;
  total_items: number;
  final_items: number;
  done_items: number;
  agreed_items: number | null;
  other_done: boolean | null;
  order: OrderEntry[];
  observer: { readers: [string, string] } | null;
}

/** How the open view names its two sides. A reader sees "you" and the other
 * reader; an observer sees the first reader's view, read-only, with both named. */
export interface Voices {
  observer: boolean;
  myCall: string;
  otherCall: string;
  editor: Record<Editor, string>;
}

const readerName = (email: string) => {
  const local = email.split("@")[0];
  return local.charAt(0).toUpperCase() + local.slice(1);
};

export function voices(project: JointReviewProject): Voices {
  if (!project.observer)
    return {
      observer: false,
      myCall: "Your call",
      otherCall: "Other reader",
      editor: { you: "you", other_reader: "other reader" },
    };
  const [first, second] = project.observer.readers.map(readerName);
  return {
    observer: true,
    myCall: `${first}'s call`,
    otherCall: second,
    editor: { you: first, other_reader: second },
  };
}

/** Nothing is shared (notes, pointer, Follow) in a blind review's independent phase. */
export const sharesState = (project: Pick<JointReviewProject, "phase"> | undefined) =>
  !!project && project.phase !== "independent";

/** The item both readers are on (or only the study). Either reader moves it. */
export interface Pointer {
  study_uid: string;
  item_id: string | null;
  updated_at: string;
  set_by_me: boolean;
}

export interface SharedText {
  text: string;
  updated_at: string | null;
  edited_by: Editor | null;
}

/** One label map for the study: a reader's, on the series all their marks are
 * in, or a blind review's markers on one series (reader null). */
export interface ReaderMask {
  mask_id: string;
  reader: Reader | null;
  series_instance_uid: string;
  mask_url: string;
}

/** A round-1 mark: `item_id` is null when both readers marked it (context only).
 * `series_instance_uid` is its mask's series. `in_mask` is false when a later
 * overlapping mark by the same reader replaced its voxels: it has a key slice
 * but no outline of its own. */
export interface Mark {
  /** Null for a blind review's marker. */
  reader: Reader | null;
  mask_id: string;
  series_instance_uid: string;
  segment_index: number;
  item_id: string | null;
  key_slice_sop_instance_uid: string;
  long_axis_mm: number | null;
  /** The reader's 1–100 rating of the stone (ENG-262); absent in worklists
   * generated before ENG-302, null when the save has none. */
  reader_confidence?: number | null;
  in_mask: boolean;
}

export interface CallView {
  call: JointCall;
  rating: number | null;
  reason: string | null;
}

export interface StudyItem {
  item_id: string;
  /** Null for a blind review's marker. */
  reader: Reader | null;
  my_call: JointCall | null;
  other_call: JointCall | null;
  consensus: JointCall | null;
  note: SharedText | null;
  final: boolean;
  done: boolean;
  phase: Phase | null;
  /** A blind review's independent calls; the other reader's only in the joint phase. */
  independent: { mine: CallView | null; other: CallView | null } | null;
}

/** GET /projects/{id}/studies/{uid}/joint-review. */
export interface JointReviewStudy {
  masks: ReaderMask[];
  marks: Mark[];
  items: StudyItem[];
}

/** The series the study opens on in the axial pane: the one most of its marks
 * are on (the readers are told to read one series, so nearly always the only
 * one), or null with no marks. A card on another series swaps it in on jump. */
export function reviewSeries(study: JointReviewStudy): string | null {
  const counts = new Map<string, number>();
  for (const { series_instance_uid: series } of study.marks)
    counts.set(series, (counts.get(series) ?? 0) + 1);
  let best: string | null = null;
  for (const [series, count] of counts)
    if (best === null || count > counts.get(best)!) best = series;
  return best;
}

export interface ItemRef {
  study_uid: string;
  item_id: string;
}

/** An item's key across the project: item ids are unique only within a study. */
export const itemKey = ({ study_uid, item_id }: ItemRef) => `${study_uid}|${item_id}`;

/** The open study's items' `done` by `itemKey`, fresher than the project listing. */
export function freshDone(studyUid: string, items: readonly StudyItem[] = []) {
  return new Map(
    items.map((item) => [itemKey({ study_uid: studyUid, item_id: item.item_id }), item.done]),
  );
}

function reviewOrder(project: JointReviewProject, fresh: ReadonlyMap<string, boolean>) {
  return project.order.map((entry) => {
    const key = itemKey(entry);
    return {
      study_uid: entry.study_uid,
      item_id: entry.item_id,
      key,
      done: fresh.get(key) ?? entry.done,
    };
  });
}

/** The next item not done after `from`, in review order, wrapping to the
 * start; null when every other item is done. `fresh` (the open study's items)
 * overrides the listing. */
export function nextUndone(
  project: JointReviewProject,
  from: ItemRef | null,
  fresh: ReadonlyMap<string, boolean> = new Map(),
): ItemRef | null {
  const order = reviewOrder(project, fresh);
  const fromKey = from ? itemKey(from) : null;
  const at = order.findIndex((item) => item.key === fromKey);
  const rotated = [...order.slice(at + 1), ...order.slice(0, at + 1)];
  const next = rotated.find((item) => !item.done && item.key !== fromKey);
  return next ? { study_uid: next.study_uid, item_id: next.item_id } : null;
}

/** Done count with the open study's items taken from their fresher state. */
export function doneCount(
  project: JointReviewProject,
  fresh: ReadonlyMap<string, boolean>,
): number {
  return reviewOrder(project, fresh).filter((item) => item.done).length;
}

/** The studies in review order (first appearance), each with its listed items. */
export function reviewStudies(project: JointReviewProject) {
  const studies = new Map<string, OrderEntry[]>();
  for (const entry of project.order)
    studies.set(entry.study_uid, [...(studies.get(entry.study_uid) ?? []), entry]);
  return [...studies].map(([study_uid, items]) => ({ study_uid, items }));
}

/** Study label by review order: "Case 3". */
export function caseLabel(project: JointReviewProject, studyUid: string): string {
  return `Case ${reviewStudies(project).findIndex((study) => study.study_uid === studyUid) + 1}`;
}

/** An item's name: "Item 37" for a blind review's marker, else its number
 * among its reader's items in the study ("Reader A · 2"). */
export function itemName(items: readonly StudyItem[], item: StudyItem): string {
  if (item.reader === null) return `Item ${item.item_id}`;
  const same = items.filter((entry) => entry.reader === item.reader);
  return `Reader ${item.reader} · ${same.indexOf(item) + 1}`;
}

/** A call as read back: "Stone present · Likely (70)" or "Indeterminate: motion". */
export function callText(view: CallView): string {
  if (view.reason) return `${callLabel(view.call)}: ${view.reason}`;
  const anchor = RATINGS.find(({ value }) => value === view.rating);
  return anchor
    ? `${callLabel(view.call)} · ${anchor.label} (${anchor.value})`
    : callLabel(view.call);
}

export type ItemState = "final" | "agreed_needs_note" | "not_agreed" | "waiting";

/** Where an item stands: final, agreed but no note yet, calls differ, or at
 * least one call missing. */
export function itemState(item: StudyItem): ItemState {
  if (item.final) return "final";
  if (item.consensus) return "agreed_needs_note";
  if (item.my_call && item.other_call) return "not_agreed";
  return "waiting";
}

export const caseUrl = (projectId: string, studyUid: string, itemId?: string | null) =>
  `/labeling/joint-review/${encodeURIComponent(projectId)}/case/${encodeURIComponent(studyUid)}${
    itemId ? `?item=${encodeURIComponent(itemId)}` : ""
  }`;

export const listUrl = (projectId: string) =>
  `/labeling/joint-review/${encodeURIComponent(projectId)}`;
